import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

/**
 * Desktop: a Mia aberta vira uma coluna a direita que empurra o conteudo. A largura sai
 * em --mia-doca (o AppShell recua o conteudo por ela) e a pessoa ajusta arrastando a
 * borda esquerda. No celular nada disso vale: o painel continua sobreposto.
 */
const CHAVE_LARGURA = 'momy.mia.largura';
const MIDIA_DESKTOP = '(min-width: 769px)';
export const LARGURA_MINIMA = 320;
const LARGURA_PADRAO = 400;
/** Espaco que sempre sobra para o menu e a tela. */
const RESERVA_CONTEUDO = 560;

export function larguraMaxima(janela = window.innerWidth): number {
  return Math.max(LARGURA_MINIMA, Math.min(900, janela - RESERVA_CONTEUDO));
}

export function limitarLargura(valor: number, janela = window.innerWidth): number {
  return Math.round(Math.min(larguraMaxima(janela), Math.max(LARGURA_MINIMA, valor)));
}

function larguraSalva(): number {
  try {
    const valor = Number(window.localStorage.getItem(CHAVE_LARGURA));
    return Number.isFinite(valor) && valor > 0 ? valor : LARGURA_PADRAO;
  } catch {
    return LARGURA_PADRAO;
  }
}

function salvarLargura(valor: number) {
  try {
    window.localStorage.setItem(CHAVE_LARGURA, String(valor));
  } catch {
    // Sem storage a largura vale so nesta visita.
  }
}

function useDesktop(): boolean {
  const consultar = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MIDIA_DESKTOP).matches
      : false;
  const [desktop, setDesktop] = useState(consultar);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const midia = window.matchMedia(MIDIA_DESKTOP);
    const mudar = () => setDesktop(midia.matches);
    midia.addEventListener('change', mudar);
    return () => midia.removeEventListener('change', mudar);
  }, []);
  return desktop;
}

export function useDocaMia(aberto: boolean, maximizado: boolean) {
  const desktop = useDesktop();
  const ancorada = aberto && desktop;
  const [largura, setLargura] = useState(larguraSalva);
  const [janela, setJanela] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  const [arrastando, setArrastando] = useState(false);
  const inicio = useRef<{ x: number; largura: number } | null>(null);

  useEffect(() => {
    const medir = () => setJanela(window.innerWidth);
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  const efetiva = maximizado ? larguraMaxima(janela) : limitarLargura(largura, janela);

  useLayoutEffect(() => {
    const raiz = document.documentElement;
    if (!ancorada) {
      raiz.style.removeProperty('--mia-doca');
      return;
    }
    raiz.style.setProperty('--mia-doca', `${efetiva}px`);
    return () => {
      raiz.style.removeProperty('--mia-doca');
    };
  }, [ancorada, efetiva]);

  const aoPressionar = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      inicio.current = { x: event.clientX, largura: efetiva };
      setArrastando(true);
    },
    [efetiva],
  );

  const aoMover = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!inicio.current) return;
    // A borda fica a esquerda: arrastar para a esquerda alarga.
    setLargura(limitarLargura(inicio.current.largura + (inicio.current.x - event.clientX)));
  }, []);

  const aoSoltar = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!inicio.current) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    inicio.current = null;
    setArrastando(false);
    setLargura((atual) => {
      salvarLargura(atual);
      return atual;
    });
  }, []);

  const aoTeclar = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const passo = event.shiftKey ? 64 : 16;
      let nova: number | null = null;
      if (event.key === 'ArrowLeft') nova = efetiva + passo;
      else if (event.key === 'ArrowRight') nova = efetiva - passo;
      else if (event.key === 'Home') nova = LARGURA_MINIMA;
      else if (event.key === 'End') nova = larguraMaxima();
      if (nova === null) return;
      event.preventDefault();
      const limitada = limitarLargura(nova);
      setLargura(limitada);
      salvarLargura(limitada);
    },
    [efetiva],
  );

  return {
    ancorada,
    largura: efetiva,
    minima: LARGURA_MINIMA,
    maxima: larguraMaxima(janela),
    arrastando,
    alca: {
      onPointerDown: aoPressionar,
      onPointerMove: aoMover,
      onPointerUp: aoSoltar,
      onPointerCancel: aoSoltar,
      onKeyDown: aoTeclar,
    },
  };
}
